"use client"

/**
 * IntercomProvider - Lazy-loaded Intercom integration
 *
 * Features:
 * - Lazy loads Intercom script only when needed (facade pattern)
 * - Supports both anonymous visitors and authenticated users
 * - Provides context for programmatic control (show, hide, showNewMessage)
 * - Handles logout properly with shutdown()
 *
 * Architecture:
 * - Shows lightweight facade button initially (no external scripts)
 * - Loads real Intercom on: user click, or after idle timeout
 * - Once loaded, facade is replaced by real Intercom launcher
 */

import { createContext, useContext, useCallback, useEffect, useRef, useState, type ReactNode } from "react"

const INTERCOM_APP_ID = "iupx6gzl"

// The signup funnel (/setup, /es/setup, ...): the open messenger covers the card form and its pay button, so
// there the landing widget never loads on its own and never opens unless the visitor asks for it.
export const SETUP_PATH = /^\/(?:[a-z]{2}(?:-[a-zA-Z]{2})?\/)?setup(?:\/|$)/

// When the visitor last asked for the messenger: our chat button or help link, Intercom's own launcher, or
// one of our show() calls (every one of them runs from a click). With blockAutoOpen, the messenger may open
// only within this window after such a request; any other opening (workspace auto-message, Fin, a reply
// arriving, a restored open state) is closed at once.
let userOpenRequestAt = 0
const USER_OPEN_WINDOW_MS = 2000
const OPEN_COMMANDS = new Set(["show", "showNewMessage", "showMessages"])
export const LAUNCHER_SELECTOR = "#intercom-facade-btn, .intercom-lightweight-app-launcher, .intercom-launcher"
// Intercom's launcher, messenger and notification frames. They are same-origin (about:blank documents the
// widget writes into), so their own documents can be listened to; their clicks never reach this window.
const INTERCOM_FRAME_SELECTOR = 'iframe[name^="intercom-"]'
const markUserOpenRequest = () => {
    userOpenRequestAt = Date.now()
}
const isActivationKey = (event: KeyboardEvent) => event.key === "Enter" || event.key === " "

// Helper to safely call Intercom (handles union type from environment.d.ts)
function callIntercom(command: string, ...args: unknown[]): void {
    if (typeof window === "undefined") return
    if (OPEN_COMMANDS.has(command)) userOpenRequestAt = Date.now()
    const intercom = window.Intercom
    if (typeof intercom === "function") {
        // Type assertion needed because Window.Intercom is a union type
        ;(intercom as (cmd: string, ...a: unknown[]) => void)(command, ...args)
    }
}

// Check if Intercom is loaded and functional
function isIntercomFunction(): boolean {
    return typeof window !== "undefined" && typeof window.Intercom === "function"
}

// Check if Intercom has booted
function isIntercomBooted(): boolean {
    if (!isIntercomFunction()) return false
    const intercom = window.Intercom as IntercomFunction
    return intercom.booted === true
}

// Context for Intercom methods
interface IntercomContextValue {
    show: () => void
    hide: () => void
    showNewMessage: (message?: string) => void
    shutdown: () => void
    update: (data: Partial<IntercomSettings>) => void
    isLoaded: boolean
    isLoading: boolean
}

const IntercomContext = createContext<IntercomContextValue | null>(null)

export function useIntercom() {
    const context = useContext(IntercomContext)
    if (!context) {
        // Return no-op functions if used outside provider (e.g., landing pages without provider)
        return {
            show: () => console.warn("[Intercom] Provider not found"),
            hide: () => {},
            showNewMessage: () => console.warn("[Intercom] Provider not found"),
            shutdown: () => {},
            update: () => {},
            isLoaded: false,
            isLoading: false,
        }
    }
    return context
}

interface IntercomProviderProps {
    children: ReactNode
    user?: {
        id: string
        email?: string
        name?: string
        createdAt?: string | Date // ISO string or Date object
        avatarUrl?: string
    }
    userHash?: string // HMAC SHA256 hash for identity verification
    hideDefaultLauncher?: boolean // Set true if using custom launcher buttons only
    loadOnIdle?: boolean // Auto-load after idle (default: true for app, false for landing)
    idleTimeout?: number // Milliseconds to wait before auto-loading (default: 8000)
    // When set, the idle auto-load waits for window `load` plus this many ms and then for a quiet
    // moment (no tap/scroll/key in the last second) before fetching the widget. Clicking the
    // launcher still loads it at once. Used on marketing pages, where Intercom's ~470 KB of
    // script was evaluating during page load (long tasks of 350 ms+ on mobile).
    deferAfterLoadMs?: number
    // With deferAfterLoadMs: also start that load (same quiet-moment wait) right after the visitor's
    // first tap, scroll or key press, so deferAfterLoadMs can be a long fallback for visitors who
    // never interact and page load itself never pays for the widget.
    loadOnFirstInteraction?: boolean
    // The messenger opens only when the visitor asks for it (launcher click or a show() call); anything
    // else that opens it is closed at once, and an open messenger is closed when this turns on. Used on
    // /setup, where the open messenger covers the card form and its pay button.
    blockAutoOpen?: boolean
}

const QUIET_INPUT_MS = 1000
const MAX_QUIET_WAITS = 10

export function IntercomProvider({
    children,
    user,
    userHash,
    hideDefaultLauncher = false,
    loadOnIdle = true,
    idleTimeout = 8000,
    deferAfterLoadMs,
    loadOnFirstInteraction = false,
    blockAutoOpen = false,
}: IntercomProviderProps) {
    const [isLoaded, setIsLoaded] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const loadAttemptedRef = useRef(false)
    const pendingActionRef = useRef<(() => void) | null>(null)
    const blockAutoOpenRef = useRef(blockAutoOpen)
    const onShowGuardRef = useRef(false)

    // Intercom keeps onShow callbacks for the page's life, across client-side navigation and this provider
    // unmounting, so the guard reads the ref (reset in the effect cleanup) and the live path: it can only ever
    // close the messenger on the setup funnel.
    const registerOnShowGuard = useCallback(() => {
        if (onShowGuardRef.current || !isIntercomFunction()) return
        onShowGuardRef.current = true
        callIntercom("onShow", () => {
            if (!blockAutoOpenRef.current || !SETUP_PATH.test(window.location.pathname)) return
            if (Date.now() - userOpenRequestAt > USER_OPEN_WINDOW_MS) callIntercom("hide")
        })
    }, [])

    useEffect(() => {
        blockAutoOpenRef.current = blockAutoOpen
        if (!blockAutoOpen) return
        if (isIntercomBooted()) {
            registerOnShowGuard()
            callIntercom("hide")
        }
        // A press, click or Enter/Space on a launcher counts as the visitor asking. The lightweight launcher and
        // our facade button live in this document; the full launcher lives in an Intercom frame, so the same
        // listeners go on every Intercom frame's document, re-attached whenever Intercom adds or rebuilds one.
        const onPointerDown = (event: Event) => {
            const target = event.target
            if (target instanceof Element && target.closest(LAUNCHER_SELECTOR)) markUserOpenRequest()
        }
        const onKeyDown = (event: KeyboardEvent) => {
            const target = event.target
            if (isActivationKey(event) && target instanceof Element && target.closest(LAUNCHER_SELECTOR)) markUserOpenRequest()
        }
        const onFrameKeyDown = (event: KeyboardEvent) => {
            if (isActivationKey(event)) markUserOpenRequest()
        }
        const pointerInputs = ["pointerdown", "click"] as const
        pointerInputs.forEach((type) => window.addEventListener(type, onPointerDown, { capture: true }))
        window.addEventListener("keydown", onKeyDown, { capture: true })

        const frameDocs = new Set<Document>()
        const watchedFrames = new Set<HTMLIFrameElement>()
        const attachFrameDoc = (frame: HTMLIFrameElement) => {
            let doc: Document | null = null
            try {
                doc = frame.contentDocument
            } catch {
                return
            }
            if (!doc || frameDocs.has(doc)) return
            frameDocs.add(doc)
            doc.addEventListener("pointerdown", markUserOpenRequest, { capture: true })
            doc.addEventListener("keydown", onFrameKeyDown, { capture: true })
        }
        const onFrameLoad = (event: Event) => attachFrameDoc(event.currentTarget as HTMLIFrameElement)
        const attachFrames = () => {
            document.querySelectorAll<HTMLIFrameElement>(INTERCOM_FRAME_SELECTOR).forEach((frame) => {
                if (!watchedFrames.has(frame)) {
                    watchedFrames.add(frame)
                    frame.addEventListener("load", onFrameLoad)
                }
                attachFrameDoc(frame)
            })
        }
        attachFrames()
        const observer = new MutationObserver(attachFrames)
        observer.observe(document.body, { childList: true, subtree: true })

        return () => {
            blockAutoOpenRef.current = false
            observer.disconnect()
            pointerInputs.forEach((type) => window.removeEventListener(type, onPointerDown, { capture: true }))
            window.removeEventListener("keydown", onKeyDown, { capture: true })
            watchedFrames.forEach((frame) => frame.removeEventListener("load", onFrameLoad))
            frameDocs.forEach((doc) => {
                doc.removeEventListener("pointerdown", markUserOpenRequest, { capture: true })
                doc.removeEventListener("keydown", onFrameKeyDown, { capture: true })
            })
        }
    }, [blockAutoOpen, registerOnShowGuard])

    // Convert user data to Intercom format
    const getIntercomSettings = useCallback((): IntercomSettings => {
        const settings: IntercomSettings = {
            app_id: INTERCOM_APP_ID,
            background_color: "#141414",
            hide_default_launcher: hideDefaultLauncher,
        }

        if (user) {
            settings.user_id = user.id
            if (user.email) settings.email = user.email
            if (user.name) settings.name = user.name
            if (user.createdAt) {
                // Convert to Unix timestamp in SECONDS (not milliseconds!)
                const date = typeof user.createdAt === "string" ? new Date(user.createdAt) : user.createdAt
                settings.created_at = Math.floor(date.getTime() / 1000)
            }
            if (userHash) {
                // user_hash is for identity verification
                ;(settings as IntercomSettings & { user_hash?: string }).user_hash = userHash
            }
        }

        return settings
    }, [user, userHash, hideDefaultLauncher])

    // Load Intercom script
    const loadIntercom = useCallback(() => {
        if (loadAttemptedRef.current || typeof window === "undefined") return
        loadAttemptedRef.current = true
        setIsLoading(true)

        // Set settings before loading
        const settings = getIntercomSettings()
        window.intercomSettings = settings

        // Create Intercom's queueing stub if not exists. It must be a FUNCTION (Intercom's own
        // snippet shape): the widget loader's onload fires before the real Intercom function
        // replaces the stub, and an object stub made onload skip boot, leaving the launcher
        // button disabled with a spinner forever. Calls made to the stub are queued and run
        // by the widget once it starts.
        if (!window.Intercom) {
            const stub = function (...args: unknown[]) {
                stub.c(args)
            } as ((...args: unknown[]) => void) & { q: unknown[]; c: (args: unknown) => void }
            stub.q = []
            stub.c = (args: unknown) => {
                stub.q.push(args)
            }
            window.Intercom = stub as unknown as typeof window.Intercom
        }
        registerSupportOnShow()

        // Load the script
        const script = document.createElement("script")
        script.type = "text/javascript"
        script.async = true
        script.src = `https://widget.intercom.io/widget/${INTERCOM_APP_ID}`

        script.onload = () => {
            // Boot Intercom
            if (isIntercomFunction()) {
                callIntercom("boot", settings)
                registerOnShowGuard()

                // Wait for boot to complete
                let booted = false
                const checkBooted = setInterval(() => {
                    if (isIntercomBooted()) {
                        booted = true
                        clearInterval(checkBooted)
                        setIsLoaded(true)
                        setIsLoading(false)

                        // Execute pending action if any
                        if (pendingActionRef.current) {
                            pendingActionRef.current()
                            pendingActionRef.current = null
                        }
                    }
                }, 100)

                // Timeout after 10 seconds
                setTimeout(() => {
                    clearInterval(checkBooted)
                    if (!booted) {
                        setIsLoading(false)
                        console.warn("[Intercom] Boot timeout")
                    }
                }, 10000)
            }
        }

        script.onerror = () => {
            console.error("[Intercom] Failed to load script")
            setIsLoading(false)
            loadAttemptedRef.current = false // Allow retry
        }

        const firstScript = document.getElementsByTagName("script")[0]
        firstScript?.parentNode?.insertBefore(script, firstScript)
    }, [getIntercomSettings, registerOnShowGuard])

    // Execute Intercom command (loads if needed)
    const executeCommand = useCallback(
        (command: string, ...args: unknown[]) => {
            if (typeof window === "undefined") return

            // If loaded, execute immediately
            if (isLoaded && isIntercomFunction()) {
                callIntercom(command, ...args)
                return
            }

            // If not loaded, load first then execute
            if (!loadAttemptedRef.current) {
                pendingActionRef.current = () => {
                    if (isIntercomFunction()) {
                        callIntercom(command, ...args)
                    }
                }
                loadIntercom()
            } else if (isLoading) {
                // Script is loading, queue the action
                pendingActionRef.current = () => {
                    if (isIntercomFunction()) {
                        callIntercom(command, ...args)
                    }
                }
            } else if (isIntercomFunction()) {
                // Load was attempted but boot was never confirmed (slow network timeout):
                // hand the command to Intercom directly instead of dropping the click.
                callIntercom(command, ...args)
            }
        },
        [isLoaded, isLoading, loadIntercom]
    )

    // Public methods
    const show = useCallback(() => executeCommand("show"), [executeCommand])
    const hide = useCallback(() => executeCommand("hide"), [executeCommand])
    const showNewMessage = useCallback(
        (message?: string) => {
            if (message) {
                executeCommand("showNewMessage", message)
            } else {
                executeCommand("show")
            }
        },
        [executeCommand]
    )

    const shutdown = useCallback(() => {
        if (isIntercomFunction()) {
            callIntercom("shutdown")
        }
    }, [])

    const update = useCallback(
        (data: Partial<IntercomSettings>) => {
            if (isLoaded && isIntercomFunction()) {
                callIntercom("update", data)
            }
        },
        [isLoaded]
    )

    // Auto-load on idle if enabled
    useEffect(() => {
        if (!loadOnIdle || loadAttemptedRef.current) return

        const loadOnIdleCallback = () => {
            if (!loadAttemptedRef.current) {
                loadIntercom()
            }
        }

        if (deferAfterLoadMs !== undefined) {
            let cancelled = false
            let timer: ReturnType<typeof setTimeout> | undefined
            let idleHandle: number | undefined
            let lastInput = 0
            let quietWaits = 0
            const onInput = (event: Event) => {
                // Same guard as onFirstInteraction: element scrolls (carousel auto-scroll) are not the visitor.
                if (event.type === "scroll" && event.target !== document) return
                lastInput = Date.now()
            }
            const inputEvents = ["pointerdown", "keydown", "scroll", "touchstart"] as const
            const interactionEvents = ["pointerdown", "keydown", "scroll", "touchstart", "wheel"] as const
            let interactionScheduled = false
            const onFirstInteraction = (event: Event) => {
                // A scroll counts only when the page itself scrolls (capture also sees scrolling inside
                // elements, e.g. the testimonials carousel's auto-scroll, which is not the visitor).
                if (event.type === "scroll" && event.target !== document) return
                if (interactionScheduled || cancelled) return
                interactionScheduled = true
                detachInteraction()
                const start = () => {
                    if (cancelled) return
                    if ("requestIdleCallback" in window) {
                        idleHandle = window.requestIdleCallback(tryLoad, { timeout: idleTimeout })
                    } else {
                        timer = setTimeout(tryLoad, QUIET_INPUT_MS)
                    }
                }
                if (document.readyState === "complete") start()
                else window.addEventListener("load", start, { once: true })
            }
            const detachInteraction = () =>
                interactionEvents.forEach((type) => window.removeEventListener(type, onFirstInteraction, { capture: true }))
            inputEvents.forEach((type) => window.addEventListener(type, onInput, { passive: true, capture: true }))
            if (loadOnFirstInteraction) {
                interactionEvents.forEach((type) => window.addEventListener(type, onFirstInteraction, { passive: true, capture: true }))
            }
            const detachInputs = () => {
                inputEvents.forEach((type) => window.removeEventListener(type, onInput, { capture: true }))
                detachInteraction()
            }

            const tryLoad = () => {
                if (cancelled || loadAttemptedRef.current) return
                if (Date.now() - lastInput < QUIET_INPUT_MS && quietWaits < MAX_QUIET_WAITS) {
                    quietWaits++
                    timer = setTimeout(tryLoad, QUIET_INPUT_MS)
                    return
                }
                detachInputs()
                loadOnIdleCallback()
            }
            const schedule = () => {
                timer = setTimeout(() => {
                    if (cancelled) return
                    if ("requestIdleCallback" in window) {
                        idleHandle = window.requestIdleCallback(tryLoad, { timeout: idleTimeout })
                    } else {
                        tryLoad()
                    }
                }, deferAfterLoadMs)
            }

            if (document.readyState === "complete") {
                schedule()
            } else {
                window.addEventListener("load", schedule, { once: true })
            }

            return () => {
                cancelled = true
                window.removeEventListener("load", schedule)
                detachInputs()
                if (timer) clearTimeout(timer)
                if (idleHandle !== undefined && "cancelIdleCallback" in window) window.cancelIdleCallback(idleHandle)
            }
        }

        if ("requestIdleCallback" in window) {
            const handle = window.requestIdleCallback(loadOnIdleCallback, { timeout: idleTimeout })
            return () => window.cancelIdleCallback(handle)
        } else {
            const timer = setTimeout(loadOnIdleCallback, idleTimeout)
            return () => clearTimeout(timer)
        }
    }, [loadOnIdle, idleTimeout, deferAfterLoadMs, loadOnFirstInteraction, loadIntercom])

    // Update user data when it changes
    useEffect(() => {
        if (isLoaded && user && isIntercomFunction()) {
            const settings = getIntercomSettings()
            callIntercom("update", settings)
        }
    }, [isLoaded, user, getIntercomSettings])

    const contextValue: IntercomContextValue = {
        show,
        hide,
        showNewMessage,
        shutdown,
        update,
        isLoaded,
        isLoading,
    }

    return (
        <>
            {/* Preconnect hints for faster loading */}
            <link rel="preconnect" href="https://widget.intercom.io" />
            <link rel="preconnect" href="https://api-iam.intercom.io" />
            <link rel="dns-prefetch" href="https://widget.intercom.io" />

            <IntercomContext.Provider value={contextValue}>{children}</IntercomContext.Provider>
        </>
    )
}

// ============================================================================
// support_opened, tracking only: classified and sent by src/lib/analytics/support-opened.ts, which loads lazily. The
// one onShow recorder below goes on when a load creates the queueing stub (the widget runs it before boot), so every
// open is seen even while the tracker's chunk is still on its way: each open is recorded at the moment it happens
// and handed to the tracker exactly once (buffered until it loads). Nothing here opens, closes or delays the messenger.
// ============================================================================
export type ChatOpenSource = "help" | "cancel" | "upgrade" | null
/** One messenger open as seen when it happened (ms timestamps, 0 = never). */
export type SupportOpen = {
    at: number
    pathname: string
    search: string
    overCardForm: boolean
    intercomFrameActive: boolean
    chatAt: number
    chatSource: ChatOpenSource
    facadeAt: number
    launcherAt: number
    frameFocusAt: number
}
const CARD_FORM_SELECTOR = 'iframe[src*="evervault"], [data-evervault]'
let lastChatOpenRequest: { at: number; source: ChatOpenSource } = { at: 0, source: null }
let facadeOpenAt = 0
let launcherAt = 0
let frameFocusAt = 0
let supportOnShowOn = false
let supportOpens: SupportOpen[] = []
let supportOpenSink: ((open: SupportOpen) => void) | null = null

/** Our facade button was pressed: the open that follows (after the widget loads) is the visitor's. */
export function markFacadeOpenRequest(): void {
    facadeOpenAt = Date.now()
}

function cardFormOnScreen(win: Window): boolean {
    const vw = win.innerWidth
    const vh = win.innerHeight
    return Array.from(win.document.querySelectorAll(CARD_FORM_SELECTOR)).some((el) => {
        const r = el.getBoundingClientRect()
        return r.width > 0 && r.height > 0 && r.bottom > 0 && r.right > 0 && r.top < vh && r.left < vw
    })
}

/**
 * Registers the onShow recorder once per page (Intercom keeps onShow callbacks for the page's life, and the landing
 * and app layouts each mount a provider). A press, click or Enter/Space on a chat launcher in this document and focus
 * moving into an Intercom frame are timed too. Returns whether it registered now.
 */
export function registerSupportOnShow(win: Window & typeof globalThis = window): boolean {
    if (supportOnShowOn || typeof win.Intercom !== "function") return false
    supportOnShowOn = true
    const onPointer = (event: Event) => {
        const target = event.target
        if (target instanceof win.Element && target.closest(LAUNCHER_SELECTOR)) launcherAt = Date.now()
    }
    const opts = { capture: true, passive: true }
    win.addEventListener("pointerdown", onPointer, opts)
    win.addEventListener("click", onPointer, opts)
    win.addEventListener("keydown", (event) => isActivationKey(event) && onPointer(event), opts)
    // Clicks inside Intercom's frames never reach this window; focus moving into one blurs it.
    win.addEventListener("blur", () => {
        frameFocusAt = Date.now()
    })
    ;(win.Intercom as (cmd: string, ...a: unknown[]) => void)("onShow", () => {
        try {
            const el = win.document.activeElement
            const open: SupportOpen = {
                at: Date.now(),
                pathname: win.location.pathname,
                search: win.location.search,
                overCardForm: cardFormOnScreen(win),
                intercomFrameActive: !!el && el.tagName === "IFRAME" && /^intercom-/.test((el as HTMLIFrameElement).name || ""),
                chatAt: lastChatOpenRequest.at,
                chatSource: lastChatOpenRequest.source,
                facadeAt: facadeOpenAt,
                launcherAt,
                frameFocusAt,
            }
            if (supportOpenSink) supportOpenSink(open)
            else if (supportOpens.length < 20) supportOpens.push(open)
        } catch {
            // Tracking never throws into Intercom.
        }
    })
    return true
}

/** The tracker takes over: every buffered open is handed to it once, later opens as they happen. */
export function setSupportOpenSink(sink: ((open: SupportOpen) => void) | null): void {
    supportOpenSink = sink
    if (sink) for (const open of supportOpens.splice(0)) sink(open)
}

/** Tests only: clears the recorder state. */
export function resetSupportOpensForTests(): void {
    facadeOpenAt = 0
    launcherAt = 0
    frameFocusAt = 0
    supportOnShowOn = false
    supportOpens = []
    supportOpenSink = null
}

// The callers' fixed opening messages: the billing cancel link, the upgrade link; no message is a help link.
function chatOpenSource(message?: string): ChatOpenSource {
    if (!message) return "help"
    if (/\bcancel/i.test(message)) return "cancel"
    if (/\bupgrad/i.test(message)) return "upgrade"
    return null
}

/**
 * Standalone function to open Intercom chat
 * Works both inside and outside the provider context
 * Falls back to clicking facade button if provider not available
 */
export function openIntercomChat(message?: string) {
    if (typeof window === "undefined") return
    lastChatOpenRequest = { at: Date.now(), source: chatOpenSource(message) }

    // Method 1: Use Intercom API if loaded
    if (isIntercomBooted()) {
        if (message) {
            callIntercom("showNewMessage", message)
        } else {
            callIntercom("show")
        }
        return
    }

    // Method 2: Click the Intercom launcher if it exists
    const launcher = document.querySelector(".intercom-launcher") as HTMLElement
    if (launcher) {
        launcher.click()
        return
    }

    // Method 3: Click facade button (for lazy-load scenarios)
    const facadeBtn = document.getElementById("intercom-facade-btn")
    if (facadeBtn) {
        facadeBtn.click()
        return
    }

    console.warn("[Intercom] No launcher found, attempting to load...")

    // Method 4: If nothing found, try to boot Intercom
    if (!window.intercomSettings) {
        window.intercomSettings = {
            app_id: INTERCOM_APP_ID,
            background_color: "#141414",
        }
    }

    // Load script dynamically
    if (!document.querySelector(`script[src*="widget.intercom.io"]`)) {
        const script = document.createElement("script")
        script.src = `https://widget.intercom.io/widget/${INTERCOM_APP_ID}`
        script.async = true
        script.onload = () => {
            if (isIntercomFunction()) {
                registerSupportOnShow()
                callIntercom("boot", window.intercomSettings)
                setTimeout(() => {
                    if (message) {
                        callIntercom("showNewMessage", message)
                    } else {
                        callIntercom("show")
                    }
                }, 500)
            }
        }
        document.body.appendChild(script)
    }
}
